// src/modules/config/components/tables/ItemsTable.tsx - ИСПРАВЛЕННАЯ ПАГИНАЦИЯ
import React from 'react';
import {
  Table,
  Space,
  Tag,
  Tooltip,
  Image,
  Button,
  Dropdown
} from 'antd';
import type { TablePaginationConfig } from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  PercentageOutlined,
  QrcodeOutlined,
  DownloadOutlined,
  EyeOutlined,
  MoreOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { MenuItem, MenuCategory } from '@/types';

interface ItemsTableProps {
  items: MenuItem[];
  categories: MenuCategory[];
  loading: boolean;
  pagination: any;
  selectedRowKeys: React.Key[];
  onSelectionChange: (selectedKeys: React.Key[]) => void;
  onEdit: (item: MenuItem) => void;
  onDelete: (itemId: number) => void;
  onCreateDiscount: (item: MenuItem) => void;
  onRemoveDiscount: (itemId: number) => void;
  onGenerateQR: (itemId: number) => void;
  onRemoveQR: (itemId: number) => void;
  onDownloadQR: (item: MenuItem) => void;
  onPaginationChange?: (page: number, pageSize: number) => void;
}

export const ItemsTable: React.FC<ItemsTableProps> = ({
  items,
  categories,
  loading,
  pagination,
  selectedRowKeys,
  onSelectionChange,
  onEdit,
  onDelete,
  onCreateDiscount,
  onRemoveDiscount,
  onGenerateQR,
  onRemoveQR,
  onDownloadQR,
  onPaginationChange
}) => {

  // ✅ ДОБАВЛЕНО: Обработчик изменения таблицы
  const handleTableChange = (paginationConfig: TablePaginationConfig) => {
    if (onPaginationChange && paginationConfig.current && paginationConfig.pageSize) {
      console.log('🔄 Изменение страницы:', paginationConfig.current, 'размер:', paginationConfig.pageSize);
      onPaginationChange(paginationConfig.current, paginationConfig.pageSize);
    }
  };

  // Функция для определения цвета тега по количеству
  const getStockColor = (value: number) => {
    if (value > 10) return 'green';
    if (value > 0) return 'orange';
    return 'red';
  };

  const columns = [
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      width: 173,
      resizable: true,
      fixed: 'left' as const,
      render: (text: string, record: MenuItem) => (
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
            <Tooltip title={text}>
              <span style={{ 
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {text}
              </span>
            </Tooltip>
          </div>
          {record.name_kz && (
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
              <Tooltip title={record.name_kz}>
                <span style={{ 
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {record.name_kz}
                </span>
              </Tooltip>
            </div>
          )}
          {record.name_en && (
            <div style={{ fontSize: '12px', color: '#999', marginBottom: '4px' }}>
              <Tooltip title={record.name_en}>
                <span style={{ 
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {record.name_en}
                </span>
              </Tooltip>
            </div>
          )}
          {record.image_url && (
            <Image
              width={40}
              height={30}
              src={record.image_url}
              alt={text}
              style={{ borderRadius: 4 }}
              preview={false}
            />
          )}
        </div>
      )
    },
    {
      title: 'Артикул',
      dataIndex: 'article',
      key: 'article',
      width: 45,
      resizable: true,
      render: (text: string) => (
        <Tooltip title={text}>
          <code style={{ fontSize: '11px' }}>{text}</code>
        </Tooltip>
      )
    },
    {
      title: 'Категории',
      dataIndex: 'category',
      key: 'category',
      width: 70,
      resizable: true,
      render: (categoryIds: number[]) => {
        if (!Array.isArray(categoryIds) || categoryIds.length === 0) {
          return <Tag color="red" style={{ fontSize: '11px' }}>Нет категорий</Tag>;
        }
        
        const categoryNames = categoryIds
          .map(id => {
            const category = categories.find(c => c.id === id);
            return category?.name;
          })
          .filter(Boolean);
        
        if (categoryNames.length === 0) {
          return <Tag color="red" style={{ fontSize: '11px' }}>Категории не найдены</Tag>;
        }
        
        const visibleCategories = categoryNames.slice(0, 2);
        const remainingCount = categoryNames.length - 2;
        
        return (
          <Tooltip title={categoryNames.join(', ')}>
            <div>
              {visibleCategories.map((name, index) => (
                <Tag key={index} color="blue" style={{ 
                  marginBottom: 2, 
                  fontSize: '11px',
                  marginRight: 2
                }}>
                  {name}
                </Tag>
              ))}
              {remainingCount > 0 && (
                <Tag color="default" style={{ fontSize: '10px' }}>
                  +{remainingCount}
                </Tag>
              )}
            </div>
          </Tooltip>
        );
      }
    },
    {
      title: 'Цена',
      dataIndex: 'price',
      key: 'price',
      width: 47,
      resizable: true,
      align: 'right' as const,
      render: (price: number, record: MenuItem) => (
        <div>
          <div style={{ fontWeight: 'bold', fontSize: '13px' }}>
            {(record.current_price || price).toLocaleString()} ₸
          </div>
          {record.has_discount && (
            <div style={{ 
              textDecoration: 'line-through', 
              color: '#999', 
              fontSize: '11px' 
            }}>
              {price.toLocaleString()} ₸
            </div>
          )}
        </div>
      ),
      sorter: (a: MenuItem, b: MenuItem) => a.price - b.price
    },
    {
      title: 'Произведено',
      dataIndex: 'total_manufactured',
      key: 'total_manufactured',
      width: 60,
      resizable: true,
      align: 'center' as const,
      render: (value: number) => (
        <Tag 
          color={getStockColor(value || 0)}
          style={{ fontSize: '11px', minWidth: '30px', textAlign: 'center' }}
        >
          {value || 0}
        </Tag>
      ),
      sorter: (a: MenuItem, b: MenuItem) => (a.total_manufactured || 0) - (b.total_manufactured || 0)
    },
    {
      title: 'Продано',
      dataIndex: 'completed_quantity',
      key: 'completed_quantity',
      width: 60,
      resizable: true,
      align: 'center' as const,
      render: (value: number) => (
        <Tag 
          color={getStockColor(value || 0)}
          style={{ fontSize: '11px', minWidth: '30px', textAlign: 'center' }}
        >
          {value || 0}
        </Tag>
      ),
      sorter: (a: MenuItem, b: MenuItem) => (a.completed_quantity || 0) - (b.completed_quantity || 0)
    },
    {
      title: 'Текущий остаток',
      dataIndex: 'current_stock',
      key: 'current_stock',
      width: 80,
      resizable: true,
      align: 'center' as const,
      render: (value: number) => (
        <Tag 
          color={getStockColor(value || 0)}
          style={{ fontSize: '11px', minWidth: '30px', textAlign: 'center' }}
        >
          {value || 0}
        </Tag>
      ),
      sorter: (a: MenuItem, b: MenuItem) => (a.current_stock || 0) - (b.current_stock || 0)
    },
    {
      title: 'Доступно для заказов',
      dataIndex: 'stock_available',
      key: 'stock_available',
      width: 80,
      resizable: true,
      align: 'center' as const,
      render: (stock: number) => (
        <Tag 
          color={getStockColor(stock || 0)}
          style={{ fontSize: '11px', minWidth: '30px', textAlign: 'center' }}
        >
          {stock || 0}
        </Tag>
      ),
      sorter: (a: MenuItem, b: MenuItem) => (a.stock_available || 0) - (b.stock_available || 0)
    },
    {
      title: 'Статус',
      key: 'status_info',
      width: 35,
      resizable: true,
      render: (_: any, record: MenuItem) => (
        <Space direction="vertical" size="small">
          <Tag 
            color={record.status === 'active' ? 'green' : 'red'}
            style={{ fontSize: '10px' }}
          >
            {record.status === 'active' ? 'Активно' : 'Снято'}
          </Tag>
          {record.has_discount && (
            <Tag 
              color="orange" 
              icon={<PercentageOutlined />}
              style={{ fontSize: '10px' }}
            >
              -{record.discount_percent}%
            </Tag>
          )}
        </Space>
      )
    },
    {
      title: 'QR',
      key: 'qr_status',
      width: 35,
      resizable: true,
      align: 'center' as const,
      render: (_: any, record: MenuItem) => (
        <div style={{ textAlign: 'center' }}>
          {record.qr_code || record.qr_code_url ? (
            <Tooltip title="QR-код создан">
              <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />
            </Tooltip>
          ) : (
            <Tooltip title="QR-код не создан">
              <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 16 }} />
            </Tooltip>
          )}
        </div>
      )
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 55,
      resizable: true,
      fixed: 'right' as const,
      render: (_: any, record: MenuItem) => {
        const menuItems: MenuProps['items'] = [
          {
            key: 'edit',
            icon: <EditOutlined />,
            label: 'Редактировать',
            onClick: () => onEdit(record)
          },
          {
            key: 'discount',
            icon: <PercentageOutlined />,
            label: record.has_discount ? 'Изменить скидку' : 'Создать скидку',
            onClick: () => onCreateDiscount(record)
          },
          ...(record.has_discount ? [{
            key: 'remove_discount',
            icon: <DeleteOutlined />,
            label: 'Удалить скидку',
            onClick: () => onRemoveDiscount(record.id)
          }] : []),
          {
            type: 'divider' as const
          },
          {
            key: 'qr_generate',
            icon: <QrcodeOutlined />,
            label: record.qr_code_url ? 'Перегенерировать QR' : 'Создать QR-код',
            onClick: () => onGenerateQR(record.id)
          },
          ...(record.qr_code_url ? [
            {
              key: 'qr_view',
              icon: <EyeOutlined />,
              label: 'Просмотреть QR',
              onClick: () => window.open(record.qr_code_url!, '_blank')
            },
            {
              key: 'qr_download',
              icon: <DownloadOutlined />,
              label: 'Скачать QR',
              onClick: () => onDownloadQR(record)
            },
            {
              key: 'qr_remove',
              icon: <DeleteOutlined />,
              label: 'Удалить QR-код',
              onClick: () => onRemoveQR(record.id),
              danger: true
            }
          ] : []),
          {
            type: 'divider' as const
          },
          {
            key: 'delete',
            icon: <DeleteOutlined />,
            label: 'Удалить блюдо',
            danger: true,
            onClick: () => onDelete(record.id)
          }
        ];

        return (
          <Space size="small">
            <Button
              type="link"
              icon={<EditOutlined />}
              size="small"
              onClick={() => onEdit(record)}
            />
            <Dropdown
              menu={{ items: menuItems }}
              trigger={['click']}
              placement="bottomRight"
            >
              <Button
                type="link"
                icon={<MoreOutlined />}
                size="small"
              />
            </Dropdown>
          </Space>
        );
      }
    }
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectionChange,
    getCheckboxProps: (record: MenuItem) => ({
      disabled: record.status === 'discontinued',
      name: record.name,
    }),
  };

  return (
    <Table
      columns={columns}
      dataSource={items}
      rowKey="id"
      loading={loading}
      rowSelection={rowSelection}
      scroll={{ 
        x: 740
      }}
      onChange={handleTableChange}
      pagination={{
        current: pagination?.page || 1,
        pageSize: pagination?.per_page || 100,
        total: pagination?.total || items.length,
        showSizeChanger: true,
        showQuickJumper: true,
        pageSizeOptions: ['20', '50', '100', '200'],
        showTotal: (total, range) => 
          `${range[0]}-${range[1]} из ${total} блюд`,
        responsive: true
      }}
      size="small"
    />
  );
};